import { useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';

import { userTypeAtom } from '../../auth/state';
import { LoadingScreen } from '../../components/LoadingScreen';
import { gracefullySetLoggerGlobalContext } from '../../datadog/logger';
import { ErrorPage } from '../../error/ErrorPage';
import { useGetTelehealthSessionInfoCallback } from '../../hooks';
import { useGetPublicProvider } from '../../hooks/useGetPublicProvider';
import { useIsValidProviderAndPatient } from '../../hooks/useIsValidProviderAndPatient';
import { useDesiredVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { useGracefullyUpdateStatsigUserCallback } from '../../statsig/useGracefullyUpdateStatsigUserCallback';
import { UserType } from '../../types';
import { useResolveClientSafetyGuardCallback } from '../../useGetClientSafetyGuard';
import { useResolvePayorSoftBlockExclusionCallback } from '../../useGetPayorSoftBlockExclusion';
import { useInitializeVideoSDKAdapter } from '../../video-sdk/actions/useLifecycleActions';
import { useGracefullyAuthorizeCurrentUserCallback } from '../auth/useGracefullyAuthorizeCurrentUserCallback';
import { useResolveStaleSoftBlockMeasuresCallback } from '../pre-session-measures/useGetStaleSoftBlockMeasures';
import { useGracefullyLoadVisitorSegmentCallback } from '../segment/useGracefullyLoadVisitorSegmentCallback';

/**
 * Handles any necessary pre-rendering tasks for the visitor route e.g. updating
 * the Statsig user. These are tasks that need to be done after the visitor user
 * info is available (i.e. route params) and before the visitor route content is
 * rendered.
 */
export function WithVisitorRoutePreRender({ children }: { children: React.ReactNode }) {
  const setUserType = useSetAtom(userTypeAtom);
  const gracefullyUpdateStatsigUser = useGracefullyUpdateStatsigUserCallback();
  const gracefullyLoadVisitorSegment = useGracefullyLoadVisitorSegmentCallback();

  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const getPublicProvider = useGetPublicProvider();
  const { loading: isValidProviderAndPatientLoading } = useIsValidProviderAndPatient(providerShortId!, patientShortId!);

  const gracefullyAuthorizeCurrentUser = useGracefullyAuthorizeCurrentUserCallback();
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter();
  const getDesiredProviderVideoPlatform = useDesiredVideoPlatformCallback();
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();
  const resolveStaleSoftBlockMeasures = useResolveStaleSoftBlockMeasuresCallback();
  const resolveClientSafetyGuard = useResolveClientSafetyGuardCallback();
  const resolvePayorSoftBlockExclusion = useResolvePayorSoftBlockExclusionCallback();

  const { loading, error } = useAsync(
    async function loadBeforeRender() {
      gracefullySetLoggerGlobalContext({ patientShortId, providerShortId });

      setUserType(UserType.CLIENT);
      const [_, publicProviderResult] = await Promise.allSettled([
        gracefullyLoadVisitorSegment({
          providerShortId,
          patientShortId,
        }),
        // initialize Statsig with a Provider's State of Operation to allow more
        // targeted rollout of Zoom vs. Twilio
        getPublicProvider(providerShortId ?? ''),
      ]);

      const publicProvider = publicProviderResult.status === 'fulfilled' ? publicProviderResult.value.data : undefined;

      await gracefullyUpdateStatsigUser({
        userID: patientShortId,
        customIDs: {
          providerShortID: providerShortId,
        },
        custom: {
          providerUsState: publicProvider?.state || 'UNKNOWN',
          userType: UserType.CLIENT,
        },
      });
      // Logic that depends on the Statsig user update 👇

      // Blocking:
      // - Video SDK initialization (error should be blocking)
      // - Loading the appointment data (gracefullyAuthorizeCurrentUser depends
      //   on it being set)
      await Promise.all([
        initializeVideoSDKAdapter(getDesiredProviderVideoPlatform()),
        getTelehealthSessionInfo({
          variables: { providerShortId, patientShortId },
        }),
      ]);

      // Resolve the soft-block eligibility inputs that come from the monolith
      // (stale measures + the under-13 safety-guard / age check + the payor
      // exclusion, e.g. KPNC) before the routing decision below
      // (gracefullyAuthorizeCurrentUser → enterWaitingRoom) so the decision reads
      // settled values instead of racing in-flight queries. This runs under the
      // LoadingScreen, so we never flash the waiting room before eligibility is
      // known; all gracefully fail safe so they can't block the session.
      await Promise.all([
        resolveStaleSoftBlockMeasures(),
        resolveClientSafetyGuard(),
        resolvePayorSoftBlockExclusion(),
      ]);

      await gracefullyAuthorizeCurrentUser({
        variables: { patientShortId: patientShortId ?? '' },
      });
    },
    // Only run once on mount
    [],
  );

  if (loading || isValidProviderAndPatientLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return children;
}
